// 视频网格：按距离加载，按播放区域控制播放。
// lazy_offset：视口上下预加载距离（px，默认 500，0 表示进入视口才加载）。
// play_zone：视口中间的播放区域比例，默认 1/3。
// play_zone=false 只关闭播放区域限制，仍需先触发懒加载。
// 沿用原组件名称，目前由浏览器直接加载，不启动 Worker。
(() => {
  // 同页多次引用脚本时，只注册一次全局事件。
  if (window.__WVG_INIT__) return;
  window.__WVG_INIT__ = true;

  const instances = new WeakMap();

  // 解析 data-* 布尔值，缺省时使用默认配置。
  function toBool(value, fallback) {
    if (value === undefined || value === null || value === "") return fallback;
    return value === true || value === "true" || value === "1";
  }

  // 地址支持 JSON 数组、单个地址或用 || 分隔的字符串。
  function parseUrls(raw) {
    if (!raw) return [];
    let parsed = raw;
    try {
      parsed = JSON.parse(raw);
    } catch (_) {}

    if (Array.isArray(parsed)) {
      return parsed.map((u) => String(u || "").trim()).filter(Boolean);
    }
    if (typeof parsed === "string") {
      return parsed
        .split("||")
        .map((u) => u.trim())
        .filter(Boolean);
    }
    return [];
  }

  // 转义写入 HTML 属性的内容。
  function escapeAttr(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  // 支持小数、百分比、分数及 false；无效比例使用默认值。
  function parsePlayZone(value, fallback) {
    if (value === undefined || value === null || value === "") return fallback;

    const raw = String(value).trim().toLowerCase();
    if (!raw) return fallback;
    if (raw === "false") return false;

    let ratio = NaN;

    if (raw.endsWith("%")) {
      ratio = Number(raw.slice(0, -1)) / 100;
    } else if (raw.includes("/")) {
      const parts = raw.split("/");
      if (parts.length === 2) {
        const numerator = Number(parts[0]);
        const denominator = Number(parts[1]);
        if (
          Number.isFinite(numerator) &&
          Number.isFinite(denominator) &&
          denominator !== 0
        ) {
          ratio = numerator / denominator;
        }
      }
    } else {
      ratio = Number(raw);
    }

    if (!Number.isFinite(ratio) || ratio <= 0 || ratio > 1) return fallback;
    return ratio;
  }

  class WorkerVideoGrid {
    constructor(root) {
      this.root = root;
      this.urls = parseUrls(root.dataset.videoUrls);
      this.autoplay = toBool(root.dataset.autoplay, true);
      this.loop = toBool(root.dataset.loop, true);
      this.muted = toBool(root.dataset.muted, true);
      this.controls = toBool(root.dataset.controls, true);
      this.poster = parseUrls(root.dataset.videoPoster);
      this.playZone = parsePlayZone(root.dataset.playZone, 1 / 3);

      // 加载距离与播放区域独立；无效距离回退为 500px。
      const rawOffset = root.dataset.lazyOffset;
      const offset =
        rawOffset == null || rawOffset.trim() === "" ? 500 : Number(rawOffset);
      this.lazyOffset = Number.isFinite(offset) && offset >= 0 ? offset : 500;
      this.pendingVideos = new Set(); // 尚未触发加载的元素。
      this.loadedVideos = new Set(); // 已设置资源地址，不代表下载完成。
      this.lazyObserver = null;
      this.destroyed = false;
      this.activeIndexes = new Set(); // 符合播放区域条件的索引，与加载状态独立。

      this.videos = [];
      this.checkRaf = 0;
      this.handleViewportChange = this.scheduleVisibilityCheck.bind(this);
      this.handleVisibilityChange = this.onVisibilityChange.bind(this);
    }

    mount() {
      this.render();
      this.observe();
    }

    // 初始不设置 src，防止渲染时提前请求视频。
    render() {
      if (!this.urls.length) {
        this.root.innerHTML = `<div class="wvg-status">no video</div>`;
        return;
      }

      this.root.innerHTML = `
        <div class="wvg-grid">
          ${this.urls
            .map((_, i) => {
              const poster = this.poster[i] || this.poster[0] || "";
              return `
              <article class="wvg-card">
                <video class="wvg-video" poster="${escapeAttr(poster)}" data-index="${i}" ${this.controls ? "controls" : ""} ${this.muted ? "muted" : ""} ${this.loop ? "loop" : ""} playsinline preload="none"></video>
              </article>
            `;
            })
            .join("")}
        </div>
      `;
    }

    // 逐个观察视频；加载触发后停止观察，播放区域检查按需保留。
    observe() {
      this.videos = Array.from(this.root.querySelectorAll(".wvg-video"));
      if (!this.videos.length) return;
      this.pendingVideos = new Set(this.videos);
      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange,
      );

      if (typeof IntersectionObserver === "function") {
        this.lazyObserver = new IntersectionObserver(
          (entries) => {
            if (this.destroyed) return;
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.loadVideo(entry.target);
            });
            this.updateActiveVideos();
          },
          { root: null, rootMargin: this.lazyOffset + "px 0px", threshold: 0 },
        );
        this.videos.forEach((video) => this.lazyObserver.observe(video));
      }

      // 滚动兜底和播放区域检查共用监听，捕获内部容器的滚动。
      if (!this.lazyObserver || this.playZone !== false) {
        document.addEventListener("scroll", this.handleViewportChange, {
          capture: true,
          passive: true,
        });
        window.addEventListener("resize", this.handleViewportChange);
        window.addEventListener("orientationchange", this.handleViewportChange);
      }
      if (!this.lazyObserver) this.checkLazyVideos();
      this.updateActiveVideos();
    }

    // 资源加载的统一入口；扩展加载策略时从这里接入。
    loadVideo(video) {
      if (this.destroyed || !this.pendingVideos.has(video)) return;
      const src = this.urls[Number(video.dataset.index)];
      if (!src) return;
      this.pendingVideos.delete(video);
      this.loadedVideos.add(video);
      if (this.lazyObserver) this.lazyObserver.unobserve(video);
      // 只在进入加载范围后交给浏览器加载，不再并行预下载整段视频。
      if (!video.getAttribute("src")) {
        video.preload = "auto";
        video.src = src;
        video.load();
      }
      if (!this.pendingVideos.size) {
        if (this.lazyObserver) this.lazyObserver.disconnect();
        if (this.playZone === false) this.removeViewportListeners();
      }
    }

    // 滚动兜底：检查视口上下扩展范围；修改距离规则时同步调整 rootMargin。
    checkLazyVideos() {
      const height =
        window.innerHeight || document.documentElement.clientHeight;
      const width = window.innerWidth || document.documentElement.clientWidth;
      this.pendingVideos.forEach((video) => {
        if (!video.getClientRects().length) return;
        const rect = video.getBoundingClientRect();
        if (
          rect.top <= height + this.lazyOffset &&
          rect.bottom >= -this.lazyOffset &&
          rect.left <= width &&
          rect.right >= 0
        )
          this.loadVideo(video);
      });
    }

    removeViewportListeners() {
      document.removeEventListener("scroll", this.handleViewportChange, true);
      window.removeEventListener("resize", this.handleViewportChange);
      window.removeEventListener(
        "orientationchange",
        this.handleViewportChange,
      );
    }

    // 页面切到后台时暂停，返回前台后重新检查。
    onVisibilityChange() {
      if (this.destroyed || document.hidden) {
        this.pauseAll();
        return;
      }
      this.scheduleVisibilityCheck();
    }

    // 将同一帧内的滚动、缩放事件合并为一次检查。
    scheduleVisibilityCheck() {
      if (this.destroyed || this.checkRaf) return;
      this.checkRaf = window.requestAnimationFrame(
        () => {
          this.checkRaf = 0;
          if (this.destroyed) return;
          if (!this.lazyObserver) this.checkLazyVideos();
          this.updateActiveVideos();
        },
      );
    }

    // 视频与视口中间区域有垂直交集，即满足播放位置条件。
    isInPlayZone(video) {
      if (this.playZone === false) return true;

      const rect = video.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || 0;
      if (!viewportHeight) return false;

      const zoneHeight = viewportHeight * this.playZone;
      const zoneTop = (viewportHeight - zoneHeight) / 2;
      const zoneBottom = zoneTop + zoneHeight;

      return rect.bottom > zoneTop && rect.top < zoneBottom;
    }

    // 取消区域限制，只标记播放资格，不触发资源加载。
    activateAllVideos() {
      this.activeIndexes.clear();
      this.videos.forEach((video) => {
        const idx = Number(video.dataset.index);
        if (!Number.isNaN(idx)) this.activeIndexes.add(idx);
      });
    }

    // 更新播放资格并同步播放状态；扩展播放规则从这里接入。
    updateActiveVideos() {
      if (document.hidden) {
        this.pauseAll();
        return;
      }

      if (this.playZone === false) {
        this.activateAllVideos();
        this.syncPlayback();
        return;
      }

      this.videos.forEach((video) => {
        const idx = Number(video.dataset.index);
        if (Number.isNaN(idx)) return;

        if (this.isInPlayZone(video)) {
          this.activeIndexes.add(idx);
        } else {
          this.activeIndexes.delete(idx);
        }
      });

      this.syncPlayback();
    }

    // 仅对已触发加载且处于播放区域的视频尝试自动播放。
    syncPlayback() {
      this.videos.forEach((video) => {
        const idx = Number(video.dataset.index);
        if (!this.loadedVideos.has(video) || !this.activeIndexes.has(idx)) {
          video.pause();
          return;
        }

        if (!this.autoplay) return;
        const p = video.play();
        if (p && typeof p.catch === "function")
          p.catch(
            () => {},
          );
      });
    }

    pauseAll() {
      this.videos.forEach((video) => video.pause());
      this.activeIndexes.clear();
    }

    // 组件卸载时释放资源；新增监听或定时任务需在此配套清理。
    destroy() {
      this.destroyed = true;
      this.pauseAll();
      if (this.lazyObserver) this.lazyObserver.disconnect();
      this.pendingVideos.clear();
      this.loadedVideos.clear();

      this.removeViewportListeners();
      document.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange,
      );

      if (this.checkRaf) {
        window.cancelAnimationFrame(this.checkRaf);
        this.checkRaf = 0;
      }
    }
  }

  // 按容器创建实例，重复挂载时跳过已有实例。
  function mount(scope = document) {
    scope.querySelectorAll("[data-worker-video-grid]").forEach((root) => {
      if (instances.has(root)) return;
      const app = new WorkerVideoGrid(root);
      app.mount();
      instances.set(root, app);
    });
  }

  // 卸载范围内的实例，释放监听与状态。
  function unmount(scope = document) {
    scope.querySelectorAll("[data-worker-video-grid]").forEach((root) => {
      const app = instances.get(root);
      if (!app) return;
      app.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => mount(), {
      once: true,
    });
  } else {
    mount();
  }
  // 兼容主题编辑器动态加载和卸载区块。
  document.addEventListener("shopify:section:load", (e) => mount(e.target));
  document.addEventListener("shopify:section:unload", (e) => unmount(e.target));
})();
